import { ProviderOutput } from '@coverright/data-access/types/medicare';
import { logEvent } from '@coverright/shared/analytics';
import { enqueueSnackbar } from 'notistack';
import { removePlansFromApolloCache } from './lib';
import { MedicareQuoteQuery, useSaveMedicareQuoteMutation } from '@coverright/data-access/medicare';
import { NonOptional } from '../../../index';

export function useSaveDoctors(quote: NonOptional<MedicareQuoteQuery['medicareQuote']>) {
  const [saveQuote] = useSaveMedicareQuoteMutation();

  const save = async (doctors: ProviderOutput[]) => {
    if (doctors.length) {
      logEvent('Doctors Added', {
        doctors: doctors.map((d) => ({
          npi: d.npi,
          addresses: d.addresses.map((a) => a.id),
        })),
        location: 'Plan finder',
      });
    }

    try {
      await saveQuote({
        variables: {
          data: {
            preferredDoctors: doctors.map((d) => ({
              npi: d.npi,
              addresses: d.addresses.map((a) => ({ addressId: a.id })),
            })),
            planYear: quote.planYear,
            county: quote.county,
            zip: quote.zip,
            id: quote.id,
          },
        },
        refetchQueries: ['medicareQuote'],
        optimisticResponse: (vars) => {
          return {
            saveMedicareQuote: {
              __typename: 'SaveMedicareQuoteOutput',
              maQuoteID: quote.id,
              clientId: quote.clientId,
            },
          };
        },
        update: (cache) => {
          for (const doctor of doctors) {
            cache.modify({
              id: cache.identify(doctor),
              fields: {
                addresses(cachedAddressesRefs, {toReference}) {
                  return doctor.addresses.map((a) => toReference({
                    __typename: 'ProviderAddressOutput',
                    id: a.id
                  }))
                },
              }
            })
          }
          cache.modify({
            id: cache.identify(quote),
            fields: {
              preferredDoctors(cachedPreferredDoctorsRefs, {toReference}) {
                return doctors.map((d) => toReference({
                  __typename: 'ProviderOutput',
                  npi: d.npi
                }))
              },
            }
          })
          removePlansFromApolloCache();
        },
      });
      enqueueSnackbar('Doctors has been saved', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Error saving doctors', { variant: 'error' });
    }
  };

  return save;
}
